/**
 * The same score read at concert pitch.
 *
 * A transposing part is written in the key its player reads and sounds somewhere else, which
 * is what every player wants and what nobody conducting wants: a score where the clarinets
 * are a tone above the flutes on the page cannot be read vertically. So an editor shows the
 * music both ways, and this is the second way -- the written document moved so that what is
 * on the page is what is heard.
 *
 * It is a **view**, not an edit. Nothing here returns a command and nothing here is undoable:
 * the document stays the written one, and a caller that wants the concert reading derives it
 * for as long as it is looking at it. That is what keeps the two from drifting apart, and it
 * is why every id is carried through unchanged -- a caret, a selection, a click on the drawn
 * page and an outside remark all still name the same note in either reading.
 *
 * Two kinds of part are deliberately left alone, which is what MuseScore and Sibelius both do:
 *
 * - **An octave-transposing part.** A piccolo is written where a reader expects to see it and
 *   a guitar is written an octave above what it sounds; moving either by an octave in the
 *   concert score makes it unreadable and tells a conductor nothing they did not know. Only
 *   the chromatic part of a transposition is taken out, so a part's `octaveChange` survives
 *   into the derived score and the notes still sound where they did.
 * - **A fretted part.** Its numbers are frets on strings, worked out from the written pitch
 *   against a tuning that is itself written pitch. Moving the notes without moving the tuning
 *   would print frets nobody can play, and moving the tuning would be inventing an instrument.
 *   None of this package's fretted presets transposes chromatically, so this is a guard rather
 *   than a compromise.
 */

import { shiftHarmony } from "./harmony";
import type { NotePitch, Transposition } from "./pitch";
import { shiftPitch } from "./pitch";
import { instrumentAt } from "./query";
import type { InstrumentChange, Part, PartMeasure, Score } from "./types";

/** Whether a transposition is one this reading takes out: the octave part is kept. */
function shifts(transposition: Transposition): boolean {
  return transposition.chromatic !== 0 || transposition.diatonic !== 0;
}

/**
 * Whether a part is one whose notes this reading moves at all.
 *
 * Everything the player picks up counts, not only what they open on: a part at concert pitch
 * that takes a clarinet for sixteen bars is written in B flat for those bars, and leaving
 * them alone would put a transposed passage in the middle of a concert score.
 */
function moves(score: Score, part: Part): boolean {
  const instrument = part.instrument;
  if (instrument.tuning !== null) return false;
  if (shifts(instrument.transposition)) return true;
  return score.measures.some((measure) => {
    const taken = part.measures[measure.id]?.instrument ?? null;
    return taken !== null && shifts(taken.transposition);
  });
}

/** A doubling as a concert score states it: the same instrument, sounding where written. */
function atPitch(taken: InstrumentChange): InstrumentChange {
  const { transposition } = taken;
  return {
    ...taken,
    transposition: { chromatic: 0, diatonic: 0, octaveChange: transposition.octaveChange },
  };
}

function sounded(pitch: NotePitch, transposition: Transposition): NotePitch {
  // A drum is a place on a staff and not a pitch: a kit does not transpose, and a part that
  // somehow declares one would still be played on the drums it names.
  if (pitch.drum !== null) return pitch;
  return shiftPitch(pitch, transposition.diatonic, transposition.chromatic);
}

function sing(measure: PartMeasure, transposition: Transposition): PartMeasure {
  return {
    ...measure,
    // A bar that hands the player another instrument still does, and still prints its name;
    // what it no longer does is ask them to read at anything but concert pitch.
    instrument: measure.instrument === null ? null : atPitch(measure.instrument),
    voices: measure.voices.map((voice) => ({
      ...voice,
      events: voice.events.map((event) =>
        event.kind === "note"
          ? { ...event, pitches: event.pitches.map((pitch) => sounded(pitch, transposition)) }
          : event,
      ),
    })),
    // A chord symbol is written in the part's own key -- a rhythm section in E flat reads its
    // chart in E flat -- so it moves with the notes under it. A figure names an interval above
    // the bass and is the same in every key, so it does not.
    harmonies: measure.harmonies.map((harmony) =>
      shiftHarmony(harmony, transposition.diatonic, transposition.chromatic),
    ),
  };
}

function sung(score: Score, part: Part): Part {
  // Bar by bar, because a part that doubles reads at one transposition and then another.
  const held = new Map<string, Transposition>();
  score.measures.forEach((measure, index) => {
    held.set(measure.id, instrumentAt(score, part, index).transposition);
  });

  const measures: Record<string, PartMeasure> = {};
  for (const [id, measure] of Object.entries(part.measures)) {
    measures[id] = sing(measure, held.get(id) ?? part.instrument.transposition);
  }
  return {
    ...part,
    instrument: {
      ...part.instrument,
      // What is left is the octave the part is written in, which is not taken out above.
      transposition: {
        chromatic: 0,
        diatonic: 0,
        octaveChange: part.instrument.transposition.octaveChange,
      },
      // The piece's key is printed over every part of a concert score, including the ones
      // that are written without a signature when they are written for their player.
      keyPrinting: "derived",
    },
    measures,
  };
}

/**
 * The score as it sounds: every transposing part moved to concert pitch, everything else
 * exactly as it stands.
 *
 * The result is an ordinary score. It can be drawn, played, exported or printed like any
 * other -- exporting one is how a concert-pitch MusicXML file is made -- and because the
 * parts that come back declare no chromatic transposition, every one of them prints the
 * piece's own key signature.
 */
export function atConcertPitch(score: Score): Score {
  if (!score.parts.some((part) => moves(score, part))) return score;
  return {
    ...score,
    parts: score.parts.map((part) => (moves(score, part) ? sung(score, part) : part)),
  };
}
